import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { WhatsAppOutlined } from "@ant-design/icons";
import { SERVICE_AREAS, NATIONAL_LINK, OFFICES } from "../../data/locations";
import HeroImg from "../../Comon/Images/GarvanaHome3.jpg";
import "./ServiceAreas.css";

const BASE_URL = "https://www.garvana.com";
const PHONE = "+919800040505";
const WA_MSG = "Hi Garvaana, I'd like a free consultation for landscape/garden services.";

const TITLE = "Service Areas — Landscape Design Across Delhi NCR & India | Garvaana";
const DESC =
  "Garvaana Enterprise provides landscape design & garden maintenance across Delhi NCR (Delhi, Noida, Gurugram, Faridabad, Ghaziabad) and 30+ cities in UP, Haryana, Rajasthan & Uttarakhand — plus Pan-India projects. Call +91-9800040505.";

/* ── Count-up hook (shares the StatsBar animation language) ── */
function useCountUp(target, active, delay = 0, duration = 1600) {
  const [display, setDisplay] = useState("0");
  useEffect(() => {
    if (!active) return;
    const match = String(target).match(/^(\d+)(.*)/);
    if (!match) { setDisplay(target); return; }
    const end = parseInt(match[1], 10);
    const suffix = match[2];
    const steps = duration / 16;
    let cur = 0;
    const start = setTimeout(() => {
      const iv = setInterval(() => {
        cur += end / steps;
        if (cur >= end) { setDisplay(`${end}${suffix}`); clearInterval(iv); }
        else setDisplay(`${Math.floor(cur)}${suffix}`);
      }, 16);
    }, delay);
    return () => clearTimeout(start);
  }, [active, target, delay, duration]);
  return display;
}

function StatCard({ value, label, sub, active, index }) {
  const shown = useCountUp(value, active, index * 160);
  return (
    <div className="sa-stat" style={{ transitionDelay: `${index * 0.1}s` }}>
      <span className="sa-stat__v">{shown}</span>
      <span className="sa-stat__l">{label}</span>
      {sub && <span className="sa-stat__sub">{sub}</span>}
    </div>
  );
}

/* ── Reveal-on-scroll wrapper ── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function RegionBlock({ group }) {
  const [ref, inView] = useInView(0.12);
  return (
    <div className={`sa-region ${inView ? "sa-region--in" : ""}`} ref={ref}>
      <div className="sa-region__head">
        <span className="sa-region__mark">✦</span>
        <h2 className="sa-region__title">{group.region}</h2>
        <span className="sa-region__count">
          {group.cities.length} {group.cities.length === 1 ? "city" : "cities"}
        </span>
      </div>
      <div className="sa-grid">
        {group.cities.map((c, i) => (
          <Link
            to={`/${c.slug}`}
            className="sa-card"
            key={c.city}
            style={{ transitionDelay: `${i * 0.05}s` }}
            aria-label={`Landscape design & garden maintenance in ${c.city}`}
          >
            <span className="sa-card__sheen" />
            <h3 className="sa-card__city">{c.city}</h3>
            <p className="sa-card__blurb">{c.blurb}</p>
            <span className="sa-card__explore">Explore {c.city} <span className="sa-card__arrow">→</span></span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function StatsBand({ totalCities }) {
  const [ref, inView] = useInView(0.3);
  const stats = [
    { v: "100+", l: "Projects", sub: "Delivered" },
    { v: `${totalCities}+`, l: "Cities", sub: "Served" },
    { v: "500+", l: "Plant Varieties", sub: "In Our Nursery" },
    { v: "10+", l: "Years", sub: "Of Excellence" },
  ];
  return (
    <div className="sa-stats-wrap">
      <section className={`sa-stats ${inView ? "sa-stats--in" : ""}`} ref={ref}>
        {stats.map((s, i) => (
          <StatCard key={i} value={s.v} label={s.l} sub={s.sub} active={inView} index={i} />
        ))}
      </section>
    </div>
  );
}

export default function ServiceAreas() {
  const navigate = useNavigate();
  const totalCities = SERVICE_AREAS.reduce((n, g) => n + g.cities.length, 0);

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Service Areas", item: `${BASE_URL}/service-areas` },
    ],
  };
  const areaSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Garvaana Enterprise",
    telephone: "+91-9800040505",
    url: `${BASE_URL}/service-areas`,
    areaServed: [
      { "@type": "Country", name: "India" },
      ...SERVICE_AREAS.flatMap((g) => g.cities.map((c) => ({ "@type": "City", name: c.city }))),
    ],
  };

  return (
    <div className="sa-page">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESC} />
        <link rel="canonical" href={`${BASE_URL}/service-areas`} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESC} />
        <meta property="og:url" content={`${BASE_URL}/service-areas`} />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
        <script type="application/ld+json">{JSON.stringify(areaSchema)}</script>
      </Helmet>

      {/* ── HERO ── */}
      <section className="sa-hero">
        <img src={HeroImg} alt="Garvaana Enterprise service areas across Delhi NCR and India" className="sa-hero__img" />
        <div className="sa-hero__overlay" />
        <div className="sa-hero__content">
          <span className="sa-hero__label">✦ Where We Work</span>
          <h1 className="sa-hero__title">Our Service Areas</h1>
          <p className="sa-hero__sub">
            Luxury landscape design &amp; garden maintenance across Delhi NCR and {totalCities}+ cities
            in North India — plus dedicated Pan-India projects.
          </p>
          <div className="sa-hero__actions">
            <a href={`tel:${PHONE}`} className="btn-luxury sa-rbtn">📞 Call +91 98000 40505</a>
            <a
              href={`https://wa.me/${PHONE.replace("+", "")}?text=${encodeURIComponent(WA_MSG)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="sa-wa-btn"
            >
              <WhatsAppOutlined /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* ── STATS (animated count-up) ── */}
      <StatsBand totalCities={totalCities} />

      {/* ── INTRO ── */}
      <section className="sa-intro">
        <div className="sa-intro__inner">
          <span className="section-label">Serving Delhi NCR &amp; Pan India</span>
          <h2 className="section-title">Find Garvaana in Your City</h2>
          <span className="gold-divider center" />
          <p className="sa-intro__p">
            From our design studio in Noida and our 500+ variety plant nursery in Gajraula, Garvaana
            Enterprise delivers complete landscape design, garden installation and year-round
            maintenance across Delhi NCR and connected states. Choose your city below for
            <strong> landscape design</strong> and <strong>garden maintenance</strong> services near you —
            or explore our <Link to={`/${NATIONAL_LINK.slug}`} className="sa-textlink">Pan-India services</Link>.
          </p>
        </div>
      </section>

      {/* ── REGION GROUPS ── */}
      <section className="sa-regions">
        <div className="sa-regions__inner">
          {SERVICE_AREAS.map((group) => (
            <RegionBlock group={group} key={group.region} />
          ))}
        </div>
      </section>

      {/* ── EVERY CITY INCLUDES — pillars ── */}
      <section className="sa-pillars">
        <div className="sa-pillars__inner">
          <span className="section-label" style={{ color: "var(--color-accent)" }}>
            The Same Standard, Everywhere
          </span>
          <h2 className="sa-pillars__title">Every City Includes</h2>
          <div className="sa-pillars__grid">
            {[
              { t: "Bespoke Design", d: "3D landscape & garden design tailored to your space." },
              { t: "Expert Installation", d: "Lawns, hardscape, irrigation, lighting & water features." },
              { t: "Garden Maintenance", d: "Monthly, quarterly & annual AMC care plans." },
              { t: "500+ Plant Nursery", d: "Premium plants from our own nursery, wholesale & retail." },
            ].map((p, i) => (
              <div className="sa-pillar" key={i}>
                <span className="sa-pillar__mark">✦</span>
                <h3 className="sa-pillar__title">{p.t}</h3>
                <p className="sa-pillar__desc">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OFFICES / MAPS ── */}
      <section className="sa-map">
        <div className="sa-map__inner">
          <span className="section-label">Visit Us</span>
          <h2 className="section-title">Our Offices</h2>
          <span className="gold-divider center" />
          <div className="sa-offices">
            {OFFICES.map((o) => (
              <div className="sa-office" key={o.key}>
                <div className="sa-office__body">
                  <span className="sa-office__tag">{o.city}</span>
                  <h3 className="sa-office__name">{o.name}</h3>
                  <p className="sa-office__addr">{o.address}</p>
                  <a href={o.mapsUrl} target="_blank" rel="noopener noreferrer" className="sa-office__dir">
                    Get Directions →
                  </a>
                </div>
                <div className="sa-map__frame">
                  <iframe
                    title={`Garvaana Enterprise — ${o.name}, ${o.city}`}
                    src={o.embed}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="sa-cta">
        <h2 className="sa-cta__title">Don't See Your City?</h2>
        <p className="sa-cta__text">
          We take on projects across India. Tell us your location — free consultation, quote within 24 hours.
        </p>
        <div className="sa-hero__actions">
          <a href={`tel:${PHONE}`} className="btn-luxury sa-rbtn">📞 Call +91 98000 40505</a>
          <a
            href={`https://wa.me/${PHONE.replace("+", "")}?text=${encodeURIComponent(WA_MSG)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="sa-wa-btn"
          >
            <WhatsAppOutlined /> WhatsApp +91 98000 40505
          </a>
          <button className="btn-luxury-outline sa-rbtn" onClick={() => navigate("/contact")}>
            Request a Free Quote
          </button>
        </div>
      </section>
    </div>
  );
}
